import type { SessionInfo } from '@sahab/contracts'
import type { QueryClient } from '@tanstack/react-query'
import { sessionKey } from '../features/auth/session'
import { isPlatformPath, PLATFORM_LOGIN } from '../features/platform/session'
import { setPasswordChangeRequiredHandler, setUnauthorizedHandler } from '../lib/api'
import type { router as appRouter } from './router'

export const CHANGE_PASSWORD_PATH = '/change-password'

/** Pages anyone may open without a session (a shared invoice): never sent to sign in. */
export function isPublicPath(pathname: string): boolean {
  return pathname.startsWith('/p/')
}

/**
 * Any API call answered with 401 means the session is gone (expired, revoked, signed out in another
 * tab): drop all cached tenant data and go to /login, keeping the current page to come back to.
 */
export function installSessionExpiryHandler(router: typeof appRouter, queryClient: QueryClient): void {
  let redirecting = false
  setUnauthorizedHandler(() => {
    const { location } = router.state
    // The super-admin console has its own session and its own sign-in page.
    const login = isPlatformPath(location.pathname) ? PLATFORM_LOGIN : '/login'
    if (redirecting || location.pathname === login || isPublicPath(location.pathname)) return
    redirecting = true
    void router
      .navigate({ to: login, search: { redirect: location.href }, replace: true })
      .then(() => queryClient.clear())
      .finally(() => {
        redirecting = false
      })
  })
  installPasswordChangeHandler(router, queryClient)
}

/**
 * A 403 PASSWORD_CHANGE_REQUIRED means an administrator set a new password for this user while the
 * tab was open. Mark the cached session so the app shell locks down, then go to the change page.
 */
export function installPasswordChangeHandler(router: typeof appRouter, queryClient: QueryClient): void {
  setPasswordChangeRequiredHandler(() => {
    queryClient.setQueryData<SessionInfo>(sessionKey, (session) =>
      session ? { ...session, user: { ...session.user, mustChangePassword: true } } : session,
    )
    const { location } = router.state
    if (location.pathname === CHANGE_PASSWORD_PATH || location.pathname === '/login') return
    void router.navigate({ to: CHANGE_PASSWORD_PATH, replace: true })
  })
}
